import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { showSuccess, showValidationError, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { payrollPaymentService, toPayrollPaymentMonthYear } from '@/services/payrollPaymentService'
import { employeesService } from '@/services/employeesService'
import type {
  PayrollPaymentVm,
  GetPaymentAmountEmployeeItem,
  PayrollPaymentDetailItem,
  PayrollPaymentLineItem,
} from '@/api/types/payroll.types'
import type { DepartmentLookup } from '@/api/types'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const PayrollPaymentDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = id === 'new' || !id
  const editId = !isNew && id ? parseInt(id, 10) : 0

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [payrollMonthYear, setPayrollMonthYear] = useState(getDefaultMonthYear())
  const [departmentId, setDepartmentId] = useState(0)
  const [paymentDate, setPaymentDate] = useState(() => {
    const d = new Date()
    return d.toISOString().slice(0, 10)
  })
  const [employeeList, setEmployeeList] = useState<GetPaymentAmountEmployeeItem[]>([])
  const [paymentAmounts, setPaymentAmounts] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveDisabled, setSaveDisabled] = useState(false)
  const [, _setLoadedDetailsForEdit] = useState<PayrollPaymentDetailItem[]>([])
  const loadedDetailsRef = useRef<PayrollPaymentDetailItem[]>([])
  const [editLines, setEditLines] = useState<PayrollPaymentLineItem[]>([])
  const [empIdToName, setEmpIdToName] = useState<Record<number, string>>({})
  const [masterAmount, setMasterAmount] = useState<number>(0)

  // Load departments
  useEffect(() => {
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [])

  // When month/year and department change, call GetPaymentAmount (create mode only)
  useEffect(() => {
    if (isNew && !branchId) return
    if (!branchId || !payrollMonthYear || payrollMonthYear.length < 7) {
      setEmployeeList([])
      setPaymentAmounts({})
      setSaveDisabled(false)
      return
    }
    if (!isNew) return
    let cancelled = false
    setEmployeeList([])
    setPaymentAmounts({})
    payrollPaymentService
      .getPaymentAmount(payrollMonthYear, departmentId, branchId)
      .then((list) => {
        if (cancelled) return
        setEmployeeList(list)
        if (list.length === 0) {
          showError('Payroll Not Generated', 'Payroll is not generated for this month or no employees found.')
          setSaveDisabled(true)
        } else {
          setSaveDisabled(false)
          const fromEdit = loadedDetailsRef.current
          const initial: Record<number, string> = {}
          list.forEach((emp) => {
            const detail = fromEdit.find((d) => d.employeeId === emp.employeeId)
            initial[emp.employeeId] = detail != null ? String(detail.amount) : String(emp.remainingAmount)
          })
          setPaymentAmounts(initial)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEmployeeList([])
          setSaveDisabled(false)
        }
      })
    return () => { cancelled = true }
  }, [isNew, branchId, payrollMonthYear, departmentId])


  // Load existing record in edit mode: getById (master) -> getLinesByPaymentId (lines) -> getPaymentAmount (names)
  useEffect(() => {
    if (isNew || !editId || !branchId) return
    let cancelled = false
    setLoading(true)
    setEditLines([])
    setEmpIdToName({})
    payrollPaymentService
      .getById(editId)
      .then((data) => {
        if (cancelled) return
        const my = data.payrollMonthYear ?? ''
        let formMonthYear = getDefaultMonthYear()
        if (my.length >= 5) {
          if (my.includes('-')) {
            const [a, b] = my.split('-')
            formMonthYear = a.length === 4 ? `${a}-${b.padStart(2, '0')}` : `${b}-${a.padStart(2, '0')}`
          } else {
            formMonthYear = my
          }
        }
        setPayrollMonthYear(formMonthYear)
        const dateVal = data.date ?? (data as any).paymentDate ?? ''
        setPaymentDate(dateVal ? dateVal.slice(0, 10) : new Date().toISOString().slice(0, 10))
        setMasterAmount(data.amount ?? 0)
        return Promise.all([
          payrollPaymentService.getLinesByPaymentId(editId),
          payrollPaymentService.getPaymentAmount(formMonthYear, 0, branchId),
        ])
      })
      .then((result: [PayrollPaymentLineItem[], GetPaymentAmountEmployeeItem[]] | undefined) => {
        if (cancelled || !result) return
        const [lines, empList] = result
        setEditLines(lines ?? [])
        const nameMap: Record<number, string> = {}
        ;(empList ?? []).forEach((e: GetPaymentAmountEmployeeItem) => {
          nameMap[e.employeeId] = e.empName ?? ''
        })
        setEmpIdToName(nameMap)
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.message || 'Failed to load payroll payment.')
          navigate('/payroll/payroll-payment')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [isNew, editId, branchId, navigate])

  const departmentOptions = useMemo(
    () => [
      { value: '0', label: 'All' },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || String(d.id),
      })),
    ],
    [departments]
  )

  const setPaymentAmountForEmployee = (employeeId: number, value: string) => {
    setPaymentAmounts((prev) => ({ ...prev, [employeeId]: value }))
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError('Please select a branch.')
      return
    }
    if (!payrollMonthYear || payrollMonthYear.length < 7) {
      await showValidationError('Please select Month/Year.')
      return
    }
    if (saveDisabled || employeeList.length === 0) {
      await showValidationError('No payroll data for this month or no employees to pay.')
      return
    }
    if (!paymentDate || paymentDate.length < 10) {
      await showValidationError('Please select Payment Date.')
      return
    }

    const details: PayrollPaymentDetailItem[] = []
    let totalAmount = 0
    for (const emp of employeeList) {
      const raw = paymentAmounts[emp.employeeId] ?? ''
      if (!raw.trim()) continue
      const amt = parseFloat(raw)
      if (Number.isNaN(amt) || amt < 0) {
        await showValidationError(`Please enter a valid amount for ${emp.empName}.`)
        return
      }
      if (amt > emp.remainingAmount) {
        await showValidationError(`Amount for ${emp.empName} cannot exceed remaining amount ${emp.remainingAmount}.`)
        return
      }
      details.push({ employeeId: emp.employeeId, amount: amt })
      totalAmount += amt
    }

    if (details.length === 0) {
      await showValidationError('Please enter at least one payment amount.')
      return
    }

    setSaving(true)
    try {
      const apiMonthYear = toPayrollPaymentMonthYear(payrollMonthYear)
      const payload: PayrollPaymentVm = {
        id: isNew ? 0 : editId,
        payrollMonthYear: apiMonthYear,
        paymentDate: new Date(paymentDate + 'T00:00:00').toISOString(),
        amount: totalAmount,
        branchId,
        payrollPaymentDetails: details,
      }
      console.log('Payroll Payment (Save/Update) Payload:', JSON.stringify(payload, null, 2))
      if (isNew) {
        await payrollPaymentService.create(payload)
        await showSuccess('Saved', 'Payroll payment saved successfully.')
      } else {
        await payrollPaymentService.update(payload)
        await showSuccess('Updated', 'Payroll payment updated successfully.')
      }
      navigate('/payroll/payroll-payment')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  const totalPaymentAmount = useMemo(() => {
    let sum = 0
    employeeList.forEach((emp) => {
      const raw = paymentAmounts[emp.employeeId] ?? ''
      const amt = parseFloat(raw)
      if (!Number.isNaN(amt) && amt > 0) sum += amt
    })
    return sum
  }, [employeeList, paymentAmounts])

  const getAmountError = (emp: GetPaymentAmountEmployeeItem): string | null => {
    const raw = paymentAmounts[emp.employeeId] ?? ''
    if (!raw.trim()) return null
    const amt = parseFloat(raw)
    if (Number.isNaN(amt) || amt < 0) return 'Invalid'
    if (amt > emp.remainingAmount) return `Max ${emp.remainingAmount}`
    return null
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title="Payroll Payment" description="Payroll payment form" />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Please select a branch to manage payroll payments.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title="Payroll Payment" description="Payroll payment form" />

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'Add Payroll Payment' : 'View Payroll Payment'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Month/Year *</Label>
                  <Input
                    type="month"
                    value={payrollMonthYear}
                    onChange={(e) => !isNew ? undefined : setPayrollMonthYear(e.target.value)}
                    readOnly={!isNew}
                    className={!isNew ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <SearchableSelect
                    options={departmentOptions}
                    value={departmentId ? String(departmentId) : '0'}
                    onValueChange={(v) => !isNew ? undefined : setDepartmentId(v ? Number(v) : 0)}
                    placeholder="All"
                    searchPlaceholder="Search..."
                    emptyMessage="No department"
                    disabled={!isNew}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => !isNew ? undefined : setPaymentDate(e.target.value)}
                    readOnly={!isNew}
                    className={!isNew ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Payroll Amount</Label>
                  <Input
                    type="text"
                    readOnly
                    value={String(isNew ? totalPaymentAmount : masterAmount)}
                    className="bg-muted"
                  />
                </div>
              </div>

              {isNew && employeeList.length > 0 && (
                <>
                  <Label>Employee payment amounts (cannot exceed remaining amount)</Label>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Employee Name
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Total Amount
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Remaining Amount
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Payment Amount *
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employeeList.map((emp) => (
                          <TableRow
                            key={emp.employeeId}
                            className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                          >
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {emp.empName}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {emp.totalAmount}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {emp.remainingAmount}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-center align-middle">
                              <div className="flex justify-center">
                                <Input
                                  type="number"
                                  min={0}
                                  max={emp.remainingAmount}
                                  step="any"
                                  placeholder="0"
                                  value={paymentAmounts[emp.employeeId] ?? ''}
                                  onChange={(e) => setPaymentAmountForEmployee(emp.employeeId, e.target.value)}
                                  className="w-28 text-center"
                                />
                              </div>
                              {getAmountError(emp) && (
                                <p className="text-xs text-destructive mt-1 text-center">{getAmountError(emp)}</p>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              {!isNew && editLines.length > 0 && (
                <>
                  <Label>Payment lines</Label>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Employee Name
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Remaining Amount
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            Payment Amount
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editLines.map((line) => (
                          <TableRow
                            key={line.id ?? line.empId}
                            className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                          >
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {empIdToName[line.empId] ?? `Employee ${line.empId}`}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {line.remainingAmount ?? '—'}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {line.amount}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => navigate('/payroll/payroll-payment')}>
                  {isNew ? 'Cancel' : 'Back'}
                </Button>
                {isNew && (
                  <Button onClick={handleSave} disabled={saving || saveDisabled}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
